import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Image, Eye, Edit, Trash2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import type { PhotoEntry } from '@/domain/rdo/schema';

interface PhotosProps {
  value: PhotoEntry[];
  onChange: (photos: PhotoEntry[]) => void;
  mode: "create" | "edit" | "view";
}

interface PhotoUpload {
  file: File;
  caption: string;
  hiddenFromReport: boolean;
}

export function PhotosModule({ value, onChange, mode }: PhotosProps) {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [uploads, setUploads] = useState<PhotoUpload[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<PhotoEntry | null>(null);

  const photos = value || [];

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const validFiles: PhotoUpload[] = [];
    
    Array.from(files).forEach(file => {
      if (file.size > 3.5 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} excede o limite de 3.5 MB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error(`Arquivo ${file.name} não é uma imagem válida`);
        return;
      }

      validFiles.push({
        file,
        caption: '',
        hiddenFromReport: false
      });
    });

    if (validFiles.length > 0) {
      setUploads(validFiles);
      setIsUploadModalOpen(true);
    }
  }, []);

  const handleConfirmUpload = useCallback(() => {
    const newPhotos: PhotoEntry[] = uploads.map(upload => ({
      id: Math.random().toString(36).substr(2, 9),
      url: URL.createObjectURL(upload.file),
      caption: upload.caption,
      source: "general",
      hiddenFromReport: upload.hiddenFromReport,
      createdAt: new Date().toISOString(),
    }));

    onChange([...photos, ...newPhotos]);
    setUploads([]);
    setIsUploadModalOpen(false);
    toast.success(`${newPhotos.length} foto(s) adicionada(s) com sucesso`);
  }, [uploads, photos, onChange]);

  const handleUpdateCaption = useCallback((index: number, caption: string) => {
    const newUploads = [...uploads];
    newUploads[index].caption = caption;
    setUploads(newUploads);
  }, [uploads]);

  const handleToggleHidden = useCallback((index: number) => {
    const newUploads = [...uploads];
    newUploads[index].hiddenFromReport = !newUploads[index].hiddenFromReport;
    setUploads(newUploads);
  }, [uploads]);

  const handleDeletePhoto = useCallback((photoId: string) => {
    onChange(photos.filter(p => p.id !== photoId));
    toast.success('Foto removida com sucesso');
  }, [photos, onChange]);

  const handleEditPhoto = useCallback((photo: PhotoEntry) => {
    setEditingPhoto({ ...photo });
    setIsEditModalOpen(true);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingPhoto) return;
    
    const updatedPhotos = photos.map(p => 
      p.id === editingPhoto.id ? editingPhoto : p
    );
    onChange(updatedPhotos);
    setIsEditModalOpen(false);
    setEditingPhoto(null);
    toast.success('Foto atualizada com sucesso');
  }, [photos, editingPhoto, onChange]);

  const openViewer = useCallback((index: number) => {
    setCurrentPhotoIndex(index);
    setIsViewerOpen(true);
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isViewerOpen) return;
    
    if (e.key === 'ArrowLeft' && currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    } else if (e.key === 'ArrowRight' && currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    } else if (e.key === 'Escape') {
      setIsViewerOpen(false);
    }
  }, [isViewerOpen, currentPhotoIndex, photos.length]);

  React.useEffect(() => {
    if (isViewerOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isViewerOpen, handleKeyDown]);

  const getSourceBadge = (source?: string) => {
    switch (source) {
      case 'activity':
        return <Badge variant="secondary">Atividade</Badge>;
      case 'occurrence':
        return <Badge variant="destructive">Ocorrência</Badge>;
      default:
        return <Badge variant="outline">Geral</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="h-5 w-5" />
          Fotos ({photos.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mode !== "view" && (
          <div className="mb-4">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              id="photo-upload"
            />
            <Button asChild variant="outline">
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Fotos
              </label>
            </Button>
          </div>
        )}

        {photos.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            Nenhuma foto adicionada
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo, index) => (
              <div key={photo.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden border">
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Foto'}
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => openViewer(index)}
                  />
                </div>
                
                {photo.caption && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {photo.caption}
                  </p>
                )}
                
                <div className="flex items-center justify-between mt-2">
                  {getSourceBadge(photo.source)}
                  
                  {mode !== "view" && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openViewer(index)}
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditPhoto(photo)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePhoto(photo.id!)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {photo.hiddenFromReport && (
                  <Badge className="absolute top-2 right-2 text-xs">
                    Oculta
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload Modal */}
        <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adicionar Fotos</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {uploads.map((upload, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex gap-4">
                    <img
                      src={URL.createObjectURL(upload.file)}
                      alt="Preview"
                      className="w-20 h-20 object-cover rounded"
                    />
                    <div className="flex-1 space-y-3">
                      <div>
                        <Label htmlFor={`caption-${index}`}>Legenda</Label>
                        <Input
                          id={`caption-${index}`}
                          value={upload.caption}
                          onChange={(e) => handleUpdateCaption(index, e.target.value)}
                          placeholder="Descreva a foto..."
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`hidden-${index}`}
                          checked={upload.hiddenFromReport}
                          onCheckedChange={() => handleToggleHidden(index)}
                        />
                        <Label htmlFor={`hidden-${index}`} className="text-sm">
                          Ocultar do relatório
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setIsUploadModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleConfirmUpload}>
                Adicionar {uploads.length} foto(s)
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Foto</DialogTitle>
            </DialogHeader>
            
            {editingPhoto && (
              <div className="space-y-4">
                <img
                  src={editingPhoto.url}
                  alt="Foto"
                  className="w-full h-48 object-cover rounded"
                />
                
                <div>
                  <Label htmlFor="edit-caption">Legenda</Label>
                  <Input
                    id="edit-caption"
                    value={editingPhoto.caption || ''}
                    onChange={(e) => setEditingPhoto({
                      ...editingPhoto,
                      caption: e.target.value
                    })}
                    placeholder="Descreva a foto..."
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-hidden"
                    checked={editingPhoto.hiddenFromReport || false}
                    onCheckedChange={(checked) => setEditingPhoto({
                      ...editingPhoto,
                      hiddenFromReport: !!checked
                    })}
                  />
                  <Label htmlFor="edit-hidden" className="text-sm">
                    Ocultar do relatório
                  </Label>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit}>
                Salvar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Viewer */}
        <Dialog open={isViewerOpen} onOpenChange={setIsViewerOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 z-10"
                onClick={() => setIsViewerOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
              
              {photos[currentPhotoIndex] && (
                <>
                  <img
                    src={photos[currentPhotoIndex].url}
                    alt={photos[currentPhotoIndex].caption || 'Foto'}
                    className="w-full h-auto max-h-[70vh] object-contain"
                  />
                  
                  <div className="p-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">
                          {photos[currentPhotoIndex].caption || 'Sem legenda'}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          {getSourceBadge(photos[currentPhotoIndex].source)}
                          {photos[currentPhotoIndex].hiddenFromReport && (
                            <Badge variant="outline">Oculta do relatório</Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={currentPhotoIndex === 0}
                          onClick={() => setCurrentPhotoIndex(currentPhotoIndex - 1)}
                        >
                          ← Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={currentPhotoIndex === photos.length - 1}
                          onClick={() => setCurrentPhotoIndex(currentPhotoIndex + 1)}
                        >
                          Próxima →
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mt-2">
                      Foto {currentPhotoIndex + 1} de {photos.length}
                    </p>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}